import React, { useState } from "react";
import {
  Stack,
  Box,
  Typography,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Modal,
  Backdrop,
} from "@mui/material";
import styled from "styled-components";
import tw from "twin.macro";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import RecruitmentProcessDetailsMobileView from "./RecruitmentProcessDetailsMobileView";
import BgTitle from "components/title/BgTitle";

// MUI Icons
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CodeIcon from "@mui/icons-material/Code";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SchoolIcon from "@mui/icons-material/School";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import FeedbackIcon from "@mui/icons-material/Feedback";
import CloseIcon from "@mui/icons-material/Close";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";

// Import images
import p1Img1 from "images/faq/demo-project/p1_01.png";
import p1Img2 from "images/faq/demo-project/p1_02.png";
import p1Img3 from "images/faq/demo-project/p1_03.png";
import p1Img4 from "images/faq/demo-project/p1_04.png";
import p2Img1 from "images/faq/demo-project/p2_01.png";
import p2Img2 from "images/faq/demo-project/p2_02.png";
import p2Img3 from "images/faq/demo-project/p2_03.png";

// Styled Components
const Container = tw.div`relative`;
const Content = tw.div`max-w-screen-xl mx-auto py-10`;

const TabsContainer = styled.div`
  ${tw`bg-white rounded-lg shadow-sm mb-6 overflow-hidden`}
  border: 1px solid #e5e7eb;
`;

const ContentContainer = tw.div`w-full`;

const ProgramCard = styled(motion.div)`
  ${tw`bg-white rounded-lg shadow-md overflow-hidden mb-10`}
  border: 1px solid #e5e7eb;
`;

const CardHeader = styled.div`
  ${tw`p-6 border-b border-gray-200`}
  background-color: #f1f5f9;
`;

const CardTitle = tw.h3`text-xl font-bold text-gray-900 flex items-center`;
const CardAuthor = tw.div`flex items-center text-sm text-gray-600 mt-3`;

const CardBody = tw.div`p-6`;

const SectionContainer = styled.div`
  ${tw`mb-6 rounded-lg overflow-hidden`}
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  transition: all 0.3s ease;
  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  }
`;

const SectionTitle = styled.div`
  ${tw`flex items-center font-medium text-gray-800 py-3 px-3 border-b border-gray-100`}
  background-color: #f8fafc;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
`;

const SectionContent = styled.div`
  ${tw`py-4 px-4 text-gray-700`}
  background-color: white;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  line-height: 1.8;
`;

const ScheduleContainer = tw.div`mt-2`;
const ScheduleTotal = tw.div`font-medium mb-4 flex items-center`;
const ScheduleItem = styled.div`
  ${tw`flex items-center py-2 mb-2`}
  border-left: 2px solid #e5e7eb;
  padding-left: 12px;
  &:hover {
    border-left: 2px solid #0c4d96;
  }
`;

const ScheduleItemNumber = tw.span`w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-800 rounded-full mr-3 text-xs font-bold`;
const ScheduleItemText = tw.span`text-gray-700 font-medium`;
const ScheduleItemDays = tw.span`text-gray-500 ml-2`;

const ImagesTitle = tw.h4`font-bold text-gray-800 mb-6 flex items-center`;
const ImagesGrid = tw.div`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6`;

const ImageCard = styled(motion.div)`
  ${tw`bg-white rounded-lg overflow-hidden shadow-sm relative`}
  border: 1px solid #e5e7eb;
`;

const ImageContainer = styled.div`
  ${tw`relative overflow-hidden h-48 cursor-pointer`}
  img {
    ${tw`w-full h-full object-cover transform transition-transform duration-500`}
  }
`;

const ZoomIconOverlay = styled.div`
  ${tw`absolute inset-0 flex items-center justify-center bg-black bg-opacity-25 opacity-0 transition-opacity duration-300`}
  &:hover {
    ${tw`opacity-100`}
  }
`;

const ImageCaption = tw.p`p-4 text-xs text-gray-600`;

const FullscreenImage = styled(Box)`
  position: relative;
  height: 90vh;
  width: 90vw;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const ImageHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background-color: #f1f5f9;
  border-bottom: 1px solid #e5e7eb;
`;

const ImageContent = styled(Box)`
  flex: 1;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8fafc;
  padding: 24px;
`;

const formatMultilineText = (text) => {
  if (!text) return "";
  return text.split("/n").map((line, index) => (
    <React.Fragment key={index}>
      {line}
      {index < text.split("/n").length - 1 && <br />}
    </React.Fragment>
  ));
};

// Main Component
const RecruitmentAppsUI = ({ isXs }) => {
  const { t, i18n } = useTranslation();
  console.log("RecruitmentAppsUI", t);
  const [tabValue, setTabValue] = useState(0);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  if (isXs) return <RecruitmentProcessDetailsMobileView />;

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenImage = (image) => {
    setSelectedImage(image);
    setOpenImageModal(true);
  };

  const handleCloseImage = () => {
    setOpenImageModal(false);
  };

  // Get app data from translations
  const getBirdHospitalApp = () => {
    const appData = t("recruitment-process-content.bird-hospital-app", {
      returnObjects: true,
    });
    const categories = t(
      "recruitment-process-content.bird-hospital-app.categories",
      { returnObjects: true }
    );

    return {
      title: appData.title,
      author: appData.author,
      year: appData.year,
      description: categories.reason.content,
      schedule: {
        total: categories.schedule.total_days,
        steps: categories.schedule.steps,
      },
      impression: categories.impression.content,
      skills: categories.skills.content,
      advice: categories.advice.content,
      images: appData.images.map((img, index) => ({
        src: [p1Img1, p1Img2, p1Img3, p1Img4][index],
        alt: img.alt,
        caption: img.caption,
      })),
    };
  };

  const getChecklistApp = () => {
    const appData = t("recruitment-process-content.checklist-app", {
      returnObjects: true,
    });
    const categories = t(
      "recruitment-process-content.checklist-app.categories",
      { returnObjects: true }
    );

    return {
      title: appData.title,
      author: appData.author,
      year: appData.year,
      description: categories.reason.content,
      schedule: {
        total: categories.schedule.total_days,
        steps: categories.schedule.steps,
      },
      impression: categories.impression.content,
      skills: categories.skills.content,
      advice: categories.advice.content,
      images: appData.images.map((img, index) => ({
        src: [p2Img1, p2Img2, p2Img3][index],
        alt: img.alt,
        caption: img.caption,
      })),
    };
  };

  const birdHospitalApp = getBirdHospitalApp();
  const checklistApp = getChecklistApp();
  const currentApp = tabValue === 0 ? birdHospitalApp : checklistApp;

  // Styled Components for the Program Card
  const AvatarIcon = styled.div`
    ${tw`flex items-center justify-center rounded-full p-2 mr-3`}
    background-color: #e1effe;
    width: 36px;
    height: 36px;
    flex-shrink: 0;
  `;

  // Render Program Card
  const renderProgramCard = (app) => {
    const commonTranslations = t("recruitment-process-content.common", {
      returnObjects: true,
    });
    const appType = tabValue === 0 ? "bird-hospital-app" : "checklist-app";
    const questionTranslations = t(
      "recruitment-process-content.common.questions",
      { returnObjects: true }
    );

    // Handle day string based on language and count
    const dayStringFunc = (days) => {
      if (i18n.language === "en") {
        return days > 1 ? "days" : "day";
      }
      return "日"; // Japanese uses 日 regardless of count
    };

    return (
      <ProgramCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <CardHeader
          sx={{
            p: 4,
            background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
            borderBottom: "3px solid #0c4d96",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <AvatarIcon style={{ backgroundColor: "#dbeafe" }}>
              <CodeIcon sx={{ color: "#0c4d96" }} />
            </AvatarIcon>
            <CardTitle>{app.title}</CardTitle>
          </Box>
          <CardAuthor>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                background: "white",
                py: 1,
                px: 2,
                borderRadius: "20px",
                width: "fit-content",
              }}
            >
              <AvatarIcon
                style={{
                  backgroundColor: "#f8fafc",
                  width: "28px",
                  height: "28px",
                }}
              >
                <PersonOutlineIcon sx={{ fontSize: 16, color: "#64748b" }} />
              </AvatarIcon>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {commonTranslations.creator} {app.author}
              </Typography>
              <Chip
                label={`${app.year}${commonTranslations.hire_year}`}
                size="small"
                sx={{
                  ml: 2,
                  backgroundColor: "#f1f5f9",
                  border: "1px solid #e2e8f0",
                  fontSize: "0.7rem",
                  height: "20px",
                  fontWeight: 500,
                }}
              />
            </Box>
          </CardAuthor>
        </CardHeader>

        <CardBody sx={{ p: 4 }}>
          {/* Images */}
          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: "12px",
              backgroundColor: "#f8fafc",
              border: "1px solid #e5e7eb",
            }}
          >
            <ImagesTitle
              sx={{
                mb: 4,
                display: "flex",
                alignItems: "center",
                paddingBottom: "12px",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <AvatarIcon style={{ backgroundColor: "#dbeafe" }}>
                <CodeIcon sx={{ color: "#3b82f6" }} />
              </AvatarIcon>
              <Typography variant="h6" fontWeight={600}>
                {t(
                  "recruitment-process-content.training-programs.screenshot-title"
                )}
              </Typography>
            </ImagesTitle>
            <ImagesGrid>
              {app.images.map((image, index) => (
                <ImageCard
                  key={index}
                  whileHover={{
                    y: -5,
                    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
                  }}
                  transition={{ duration: 0.3 }}
                  sx={{ borderRadius: "8px", overflow: "hidden" }}
                >
                  <ImageContainer onClick={() => handleOpenImage(image)}>
                    <motion.img
                      src={image.src}
                      alt={image.alt}
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.5 }}
                    />
                    <ZoomIconOverlay>
                      <Box
                        sx={{
                          backgroundColor: "white",
                          borderRadius: "50%",
                          width: "48px",
                          height: "48px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        <ZoomOutMapIcon sx={{ color: "#0c4d96" }} />
                      </Box>
                    </ZoomIconOverlay>
                  </ImageContainer>
                  <ImageCaption
                    sx={{
                      p: 2,
                      backgroundColor: "white",
                      borderTop: "1px solid #e5e7eb",
                    }}
                  >
                    {image.caption}
                  </ImageCaption>
                </ImageCard>
              ))}
            </ImagesGrid>
          </Box>

          {/* Reason */}
          <SectionContainer>
            <SectionTitle sx={{ p: 2, backgroundColor: "#f8fafc" }}>
              <div className="flex items-center">
                <AvatarIcon style={{ backgroundColor: "#dbeafe" }}>
                  <LightbulbIcon fontSize="small" sx={{ color: "#3b82f6" }} />
                </AvatarIcon>
                <span>{questionTranslations.reason}</span>
              </div>
            </SectionTitle>
            <SectionContent sx={{ p: 3, lineHeight: 1.8 }}>
              {formatMultilineText(app.description)}
            </SectionContent>
          </SectionContainer>

          {/* Schedule */}
          <SectionContainer>
            <SectionTitle sx={{ p: 2, backgroundColor: "#f8fafc" }}>
              <div className="flex items-center">
                <AvatarIcon style={{ backgroundColor: "#dbeafe" }}>
                  <CalendarMonthIcon
                    fontSize="small"
                    sx={{ color: "#3b82f6" }}
                  />
                </AvatarIcon>
                <span>{questionTranslations.schedule}</span>
              </div>
            </SectionTitle>
            <SectionContent sx={{ p: 3 }}>
              <ScheduleContainer>
                <ScheduleTotal>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: "#eff6ff",
                      p: 2,
                      borderRadius: "8px",
                      mb: 3,
                    }}
                  >
                    <AccessTimeIcon
                      sx={{ mr: 2, color: "#3b82f6", fontSize: 20 }}
                    />
                    <Typography fontWeight={500}>
                      {t(
                        `recruitment-process-content.${appType}.categories.schedule.development_period`
                      )}
                    </Typography>
                  </Box>
                </ScheduleTotal>
                <Box sx={{ px: 2 }}>
                  {app.schedule.steps.map((step, index) => (
                    <ScheduleItem key={index} sx={{ mb: 2 }}>
                      <ScheduleItemNumber
                        sx={{
                          backgroundColor: "#dbeafe",
                          color: "#1e40af",
                          width: "28px",
                          height: "28px",
                        }}
                      >
                        {index + 1}
                      </ScheduleItemNumber>
                      <ScheduleItemText>{step.name}</ScheduleItemText>
                      <ScheduleItemDays>
                        ：{step.days} {dayStringFunc(step.days)}
                      </ScheduleItemDays>
                    </ScheduleItem>
                  ))}
                </Box>
              </ScheduleContainer>
            </SectionContent>
          </SectionContainer>

          {/* Impression */}
          <SectionContainer>
            <SectionTitle sx={{ p: 2, backgroundColor: "#f8fafc" }}>
              <div className="flex items-center">
                <AvatarIcon style={{ backgroundColor: "#dbeafe" }}>
                  <FeedbackIcon fontSize="small" sx={{ color: "#3b82f6" }} />
                </AvatarIcon>
                <span>{questionTranslations.impression}</span>
              </div>
            </SectionTitle>
            <SectionContent sx={{ p: 3, lineHeight: 1.8 }}>
              {formatMultilineText(app.impression)}
            </SectionContent>
          </SectionContainer>

          {/* Skills */}
          <SectionContainer>
            <SectionTitle sx={{ p: 2, backgroundColor: "#f8fafc" }}>
              <div className="flex items-center">
                <AvatarIcon style={{ backgroundColor: "#dbeafe" }}>
                  <SchoolIcon fontSize="small" sx={{ color: "#3b82f6" }} />
                </AvatarIcon>
                <span>{questionTranslations.skills}</span>
              </div>
            </SectionTitle>
            <SectionContent sx={{ p: 3, lineHeight: 1.8 }}>
              {app.skills}
            </SectionContent>
          </SectionContainer>

          {/* Advice */}
          <SectionContainer>
            <SectionTitle sx={{ p: 2, backgroundColor: "#f8fafc" }}>
              <div className="flex items-center">
                <AvatarIcon style={{ backgroundColor: "#dbeafe" }}>
                  <LightbulbIcon fontSize="small" sx={{ color: "#3b82f6" }} />
                </AvatarIcon>
                <span>{questionTranslations.advice}</span>
              </div>
            </SectionTitle>
            <SectionContent sx={{ p: 3, lineHeight: 1.8 }}>
              {app.advice}
            </SectionContent>
          </SectionContainer>
        </CardBody>
      </ProgramCard>
    );
  };

  return (
    <>
      <Container>
        <Content>
          <Stack sx={{ mb: 4 }}>
            <BgTitle
              isCenter={true}
              text={t("recruitment-process-content.training-programs.title")}
            />
            <Typography
              variant="subtitle1"
              sx={{
                textAlign: "center",
                mt: 2,
                color: "#64748b",
                maxWidth: "800px",
                mx: "auto",
              }}
            >
              {t("recruitment-process-content.additional_msg_subtitle")}
              <br />
              {t("recruitment-process-content.additional_msg_description")}
            </Typography>
          </Stack>

          <TabsContainer>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              centered
              sx={{
                "& .MuiTabs-indicator": {
                  backgroundColor: "#0c4d96",
                  height: "3px",
                },
                "& .Mui-selected": {
                  color: "#0c4d96 !important",
                  fontWeight: "bold",
                  backgroundColor: "rgba(219, 234, 254, 0.4)",
                },
                "& .MuiTab-root": {
                  minHeight: "64px",
                  fontSize: "0.9rem",
                  fontWeight: "500",
                  letterSpacing: "0.02em",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "rgba(241, 245, 249, 0.8)",
                  },
                },
              }}
            >
              <Tab
                label={t(
                  "recruitment-process-content.training-programs.tabs.bird-hospital"
                )}
                icon={
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: tabValue === 0 ? "#dbeafe" : "#f1f5f9",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <CodeIcon
                      sx={{
                        fontSize: 16,
                        color: tabValue === 0 ? "#0c4d96" : "#64748b",
                      }}
                    />
                  </Box>
                }
                iconPosition="start"
              />
              <Tab
                label={t(
                  "recruitment-process-content.training-programs.tabs.checklist"
                )}
                icon={
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: tabValue === 1 ? "#dbeafe" : "#f1f5f9",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <PersonOutlineIcon
                      sx={{
                        fontSize: 16,
                        color: tabValue === 1 ? "#0c4d96" : "#64748b",
                      }}
                    />
                  </Box>
                }
                iconPosition="start"
              />
            </Tabs>
          </TabsContainer>
          <ContentContainer>{renderProgramCard(currentApp)}</ContentContainer>

          {/* Fullscreen Image Modal */}
          <Modal
            open={openImageModal}
            onClose={handleCloseImage}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
              timeout: 500,
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 4,
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
            >
              {selectedImage && (
                <FullscreenImage>
                  <ImageHeader>
                    <Typography variant="h6" fontWeight={600}>
                      {selectedImage.alt}
                    </Typography>
                    <IconButton
                      onClick={handleCloseImage}
                      size="large"
                      sx={{
                        color: "#64748b",
                        "&:hover": { color: "#0c4d96" },
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </ImageHeader>
                  <ImageContent>
                    <Box
                      component="img"
                      src={selectedImage.src}
                      alt={selectedImage.alt}
                      sx={{
                        maxWidth: "100%",
                        maxHeight: "calc(90vh - 120px)",
                        objectFit: "contain",
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                      }}
                    />
                  </ImageContent>
                  <Box sx={{ p: 3, borderTop: "1px solid #e5e7eb" }}>
                    <Typography variant="body2" color="text.secondary">
                      {selectedImage.caption}
                    </Typography>
                  </Box>
                </FullscreenImage>
              )}
            </motion.div>
          </Modal>
        </Content>
      </Container>
    </>
  );
};

export default RecruitmentAppsUI;
